import { post, get, put } from "@/api";

export interface OrderListGoods {
  id?: number | string;
  title?: string;
  m_pic?: string;
  [key: string]: unknown;
}

export interface OrderListSpec {
  id?: number | string;
  name?: string;
  pic?: string | null;
  is_outside?: boolean | number | string;
  [key: string]: unknown;
}

export interface OrderListPurchasePlan {
  matched?: boolean;
  apply_month?: string | number;
  flow_item_id?: number | string;
  flow_id?: number | string;
  control_type?: number | string;
  control_type_text?: string;
  item_mode?: string;
  plan_quantity?: number | string;
  used_quantity?: number | string;
  return_quantity?: number | string;
  remain_quantity?: number | string;
  net_used_quantity?: number | string;
  /** 最小单位口径 */
  base_plan_quantity?: number | string;
  base_used_quantity?: number | string;
  base_return_quantity?: number | string;
  base_remain_quantity?: number | string;
  base_net_used_quantity?: number | string;
  multiple_unit?: number | string;
  plan_amount?: number | string;
  used_amount?: number | string;
  return_amount?: number | string;
  remain_amount?: number | string;
  order_quantity?: number | string;
}

/** 解析计划数量（支持多单位小数，如 5.16 盒） */
export function parsePlanQuantity(value: number | string | null | undefined): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** 展示计划数量：整数原样，小数保留 2 位 */
export function formatPlanQuantity(value: number | string | null | undefined): string {
  const n = parsePlanQuantity(value);
  if (Number.isInteger(n)) return String(n);
  return n.toFixed(2);
}

export interface OrderListItem {
  id: number | string;
  order_id?: number | string;
  goods_id?: number | string;
  spec_id?: number | string;
  goods_name?: string;
  sku_name?: string;
  /** 规格编码 */
  sku?: string;
  unit?: string;
  quantity?: number | string;
  price?: number | string;
  amount?: number | string;
  remark?: string;
  /** 是否清单外（下单快照 order_lists.isoutside） */
  isoutside?: boolean | number | string;
  goods?: OrderListGoods | null;
  spec?: OrderListSpec | null;
  /** 采购计划匹配用量（科室开启采购控制时） */
  purchase_plan?: OrderListPurchasePlan | null;
  [key: string]: unknown;
}

export interface OrderDepartment {
  id?: number | string;
  title?: string;
  [key: string]: unknown;
}

/** @deprecated 详情接口已改为 department，列表接口仍可能返回 office */
export type OrderOffice = OrderDepartment;

/** 订单审批流节点：approve_state 0待审批 / 1已审批 / 2已拒绝 */
export interface OrderFlow {
  id?: number | string;
  step_name?: string;
  step?: string;
  account_id?: number | string;
  account_name?: string | null;
  account_names?: string | null;
  account_ids?: string | null;
  approve?: string | null;
  approve_state?: number | string;
  approve_time?: string | null;
  sort?: number | string;
  now_step_sort?: number | string;
  is_over?: number | string;
  [key: string]: unknown;
}

export interface OrderRecord {
  id: number | string;
  order_sn?: string;
  state?: string;
  /** 1 = 退货订单 */
  is_backorder?: number | string;
  account_name?: string;
  account_id?: number | string;
  department_id?: number | string;
  amount?: number | string;
  total?: number | string;
  remark?: string;
  created_at?: string;
  approve_time?: string;
  /** 是否已打印单据 */
  is_print?: boolean | number | string;
  /** 单据打印时间 */
  print_time?: string | null;
  delivery_time?: string;
  receive_time?: string;
  complete_time?: string;
  /** 是否代下单 */
  is_divide?: number | string | boolean;
  divide_account_id?: number | string;
  /** 代下单人账号 */
  divider?: {
    id?: number | string;
    nickname?: string | null;
    name?: string | null;
    account?: string | null;
    [key: string]: unknown;
  } | null;
  lists?: OrderListItem[];
  flows?: OrderFlow[];
  department?: OrderDepartment | null;
  /** 列表接口仍可能返回 office（与 department 同结构） */
  office?: OrderDepartment | null;
  /** 签收凭证图片 */
  signimgs?: Array<{
    id?: number | string;
    order_id?: number | string;
    uri?: string;
    [key: string]: unknown;
  }>;
  /** 订单科室是否开启采购计划控制 */
  is_purchase_plan_enabled?: boolean | number | string;
  /** 企业售后期限天数（接口实时读取 enterprise.latest） */
  after_sale_days?: number | string;
  /** 是否仍在售后申请期限内（后端按签收/完成时间 + latest 计算） */
  can_apply_after_sale?: boolean | number | string;
  [key: string]: unknown;
}

export interface OrderPagesParams {
  page?: number;
  pagenumber?: number;
  search?: string;
  date_range?: string[];
  office_ids?: Array<number | string>;
  /** 物资规格 ID 筛选 */
  spec_ids?: Array<number | string>;
  state?: string[];
  /** 我的代办：仅返回当前账号待审批订单 */
  is_todo?: boolean;
}

export interface OrderPagesResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: OrderRecord[];
  total?: number;
  page?: number;
  pagenumber?: number;
  /** 当前企业售后期限天数 */
  after_sale_days?: number | string;
}

/** 订单分页列表 */
export function getOrderPages(params: OrderPagesParams = {}) {
  return post<OrderPagesResponse>("/platform/order/pages", params);
}

export interface OrderApproveParams {
  order_id: number | string;
  /** true 同意，false 拒绝 */
  is_agree: boolean;
  remark?: string;
}

export interface OrderApproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
}

/** 订单审批 */
export function approveOrder(params: OrderApproveParams) {
  return put<OrderApproveResponse>("/platform/order/approve", params);
}

export interface OrderBatchApproveParams {
  order_ids: Array<number | string>;
  /** true 同意，false 拒绝 */
  is_agree: boolean;
  remark?: string;
}

export interface OrderBatchApproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: unknown;
}

/** 订单批量审批（同意 / 拒绝） */
export function batchApproveOrder(params: OrderBatchApproveParams) {
  return put<OrderBatchApproveResponse>("/platform/order/batch-approve", params);
}

export interface OrderUnapproveParams {
  order_id: number | string;
  remark?: string;
}

export interface OrderUnapproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
}

/** 订单反审 */
export function unapproveOrder(params: OrderUnapproveParams) {
  return put<OrderUnapproveResponse>("/platform/order/unapprove", params);
}

export interface OrderBatchUnapproveParams {
  order_ids: Array<number | string>;
  remark?: string;
}

export interface OrderBatchUnapproveResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: unknown;
}

/** 订单批量反审 */
export function batchUnapproveOrder(params: OrderBatchUnapproveParams) {
  return put<OrderBatchUnapproveResponse>("/platform/order/batch-unapprove", params);
}

export interface OrderDetailResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: OrderRecord;
  /** 当前企业售后期限天数 */
  after_sale_days?: number | string;
}

/** 订单详情 */
export function getOrderDetail(id: number | string) {
  return get<OrderDetailResponse>(`/platform/order/detail/${id}`);
}

export interface OrderEditListItem {
  goods_id: number | string;
  spec_id: number | string;
  quantity: number;
  remark?: string;
}

export interface OrderEditParams {
  order_id: number | string;
  lists: OrderEditListItem[];
  /** 订单备注（可选） */
  remark?: string;
  /** 使用科室（可选） */
  department_id?: number | string;
  /** 下单人（可选，须属于目标科室） */
  account_id?: number | string;
  /** 下单时间（可选，格式 Y-m-d H:i:s） */
  created_at?: string;
}

export interface OrderEditResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  data?: OrderRecord;
  errors?: Record<string, string[]>;
}

/** 修改订单物资明细 */
export function editOrder(params: OrderEditParams) {
  return put<OrderEditResponse>("/platform/order/edit", params);
}

export interface OrderSignParams {
  order_id: number | string;
  /** 已上传的签收图片 URL 列表，最多 5 张 */
  images?: string[];
}

export interface OrderSignResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
}

/** 订单签收 / 代收 */
export function signOrder(params: OrderSignParams) {
  return put<OrderSignResponse>("/platform/order/sign", params);
}

export interface OrderCancelParams {
  order_id: number | string;
  /** 取消原因 */
  remark: string;
}

export interface OrderCancelResponse {
  error: boolean | number;
  message?: string;
  msg?: string;
  errors?: Record<string, string[]>;
}

/** 取消订单 */
export function cancelOrder(params: OrderCancelParams) {
  return put<OrderCancelResponse>("/platform/order/cancel", params);
}
